import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { SlidersHorizontal, X, Search, ChevronDown } from "lucide-react";
import ProductCard from "@/components/ProductCard";

import { API_BASE as API } from "@/config/api";

const OCCASIONS = ["Birthday","Anniversary","Wedding","Diwali","Valentine's Day","Eid","Christmas","Corporate","Baby Shower","Engagement"];
const SORT_OPTIONS = [
  { value: "latest", label: "Latest" },
  { value: "popular", label: "Most Popular" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
];

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filter state
  const [selectedCategories, setSelectedCategories] = useState(() => {
    const c = searchParams.get("category");
    return c ? [c] : [];
  });
  const [selectedOccasions, setSelectedOccasions] = useState(() => {
    const o = searchParams.get("occasion");
    return o ? [o] : [];
  });
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [sort, setSort] = useState(searchParams.get("sort") || "latest");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategories.length === 1) params.set("category", selectedCategories[0]);
      if (selectedOccasions.length === 1) params.set("occasion", selectedOccasions[0]);
      if (priceRange[0] > 0) params.set("min_price", priceRange[0]);
      if (priceRange[1] < 10000) params.set("max_price", priceRange[1]);
      if (sort) params.set("sort", sort);
      if (searchQuery) params.set("search", searchQuery);
      const featured = searchParams.get("featured");
      if (featured) params.set("featured", featured);
      const bestseller = searchParams.get("bestseller");
      if (bestseller) params.set("bestseller", bestseller);
      const isNew = searchParams.get("is_new");
      if (isNew) params.set("is_new", isNew);
      const { data } = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(data.products || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedCategories, selectedOccasions, priceRange, sort, searchQuery, searchParams]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const toggleCategory = (cat) => {
    setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };
  const toggleOccasion = (occ) => {
    setSelectedOccasions(prev => prev.includes(occ) ? prev.filter(o => o !== occ) : [...prev, occ]);
  };
  const clearFilters = () => {
    setSelectedCategories([]); setSelectedOccasions([]);
    setPriceRange([0, 10000]); setSearchQuery(""); setSearchInput("");
    setSearchParams({});
  };

  const hasFilters = selectedCategories.length > 0 || selectedOccasions.length > 0 || searchQuery || priceRange[0] > 0 || priceRange[1] < 10000;

  const FilterSidebar = () => (
    <div className="space-y-6">
      {hasFilters && (
        <button onClick={clearFilters} data-testid="clear-filters-btn"
          className="flex items-center gap-2 text-red-400 font-outfit text-xs uppercase tracking-widest hover:text-red-500">
          <X className="w-3 h-3" /> Clear All Filters
        </button>
      )}
      {/* Category */}
      <div>
        <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-petal-olive mb-4">Category</h3>
        <div className="space-y-2">
          {categories.length === 0 ? (
            <p className="font-outfit text-xs text-petal-muted">No categories yet</p>
          ) : categories.map(cat => (
            <label key={cat.id || cat.name} className="flex items-center gap-2 cursor-pointer group" data-testid={`cat-filter-${cat.name}`}>
              <input type="checkbox" checked={selectedCategories.includes(cat.name)} onChange={() => toggleCategory(cat.name)}
                className="w-3 h-3 accent-petal-olive shrink-0" />
              {cat.image && (
                <img src={cat.image} alt="" className="w-8 h-8 object-cover border border-petal-border shrink-0" />
              )}
              <span className="font-outfit text-sm text-petal-olive-text group-hover:text-petal-olive transition-colors line-clamp-1">{cat.name}</span>
            </label>
          ))}
        </div>
      </div>
      {/* Occasion */}
      <div>
        <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-petal-olive mb-4">Occasion</h3>
        <div className="space-y-2">
          {OCCASIONS.map(occ => (
            <label key={occ} className="flex items-center gap-2 cursor-pointer group">
              <input type="checkbox" checked={selectedOccasions.includes(occ)} onChange={() => toggleOccasion(occ)}
                className="w-3 h-3 accent-petal-olive" />
              <span className="font-outfit text-sm text-petal-olive-text group-hover:text-petal-olive transition-colors">{occ}</span>
            </label>
          ))}
        </div>
      </div>
      {/* Price */}
      <div>
        <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-petal-olive mb-4">Price Range</h3>
        <div className="space-y-3">
          {[[0,999,"Under ₹999"],[999,1999,"₹999 - ₹1,999"],[1999,3999,"₹1,999 - ₹3,999"],[3999,10000,"Above ₹3,999"]].map(([min,max,label]) => (
            <label key={label} className="flex items-center gap-2 cursor-pointer group">
              <input type="radio" name="price" checked={priceRange[0]===min && priceRange[1]===max} onChange={() => setPriceRange([min,max])}
                className="w-3 h-3 accent-petal-olive" />
              <span className="font-outfit text-sm text-petal-olive-text group-hover:text-petal-olive transition-colors">{label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-petal-beige" data-testid="shop-page">
      {/* Header */}
      <div className="bg-[#F5F0E8] py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <p className="font-dancing text-petal-gold text-xl mb-2">Browse Our Collection</p>
          <h1 className="font-playfair text-4xl sm:text-5xl text-petal-olive-text mb-4">
            {searchQuery ? `Search: "${searchQuery}"` : "All Gifts"}
          </h1>
          <p className="font-outfit text-sm text-petal-muted">{total} products found</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {/* Search + Sort bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <form onSubmit={(e) => { e.preventDefault(); setSearchQuery(searchInput); }} className="flex-1 flex gap-2">
            <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
              placeholder="Search gifts..." data-testid="shop-search-input"
              className="flex-1 px-4 py-2.5 border border-petal-border bg-white font-outfit text-sm focus:outline-none focus:border-petal-olive" />
            <button type="submit" className="px-4 py-2.5 bg-petal-olive text-white hover:bg-petal-olive-dark transition-colors">
              <Search className="w-4 h-4" />
            </button>
          </form>
          <div className="flex gap-3">
            <div className="relative">
              <select value={sort} onChange={e => setSort(e.target.value)} data-testid="sort-select"
                className="appearance-none pl-4 pr-10 py-2.5 border border-petal-border bg-white font-outfit text-sm focus:outline-none focus:border-petal-olive cursor-pointer">
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-petal-muted pointer-events-none" />
            </div>
            <button onClick={() => setFiltersOpen(!filtersOpen)} data-testid="mobile-filters-btn"
              className="md:hidden flex items-center gap-2 px-4 py-2.5 border border-petal-border bg-white font-outfit text-sm">
              <SlidersHorizontal className="w-4 h-4" /> Filters
            </button>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden md:block w-64 shrink-0">
            <div className="bg-white border border-petal-border p-6 sticky top-24">
              <h2 className="font-playfair text-xl text-petal-olive-text mb-6">Filters</h2>
              <FilterSidebar />
            </div>
          </aside>

          {/* Mobile Filters Drawer */}
          {filtersOpen && (
            <div className="fixed inset-0 z-50 md:hidden">
              <div className="absolute inset-0 bg-black/30" onClick={() => setFiltersOpen(false)} />
              <div className="absolute right-0 top-0 bottom-0 w-72 bg-white p-6 overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-playfair text-xl text-petal-olive-text">Filters</h2>
                  <button onClick={() => setFiltersOpen(false)}><X className="w-5 h-5" /></button>
                </div>
                <FilterSidebar />
              </div>
            </div>
          )}

          {/* Products */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => <div key={i} className="bg-white border border-petal-border h-72 animate-pulse" />)}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <p className="font-playfair text-2xl text-petal-muted mb-4">No products found</p>
                <p className="font-outfit text-sm text-petal-muted mb-8">Try adjusting your filters or search query</p>
                <button onClick={clearFilters} className="btn-primary">Clear Filters</button>
              </div>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid="product-grid">
                {products.map((product, i) => (
                  <motion.div key={product.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
